import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { size } from 'lodash';
import cx from 'classnames';

// core components
import { useConfirm } from 'material-ui-confirm';
import { Card, CardContent, Divider, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import PillButton from 'components/PillButton';

import FolderIcon from '@material-ui/icons/Folder';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import VideocamIcon from '@material-ui/icons/Videocam';
import EventIcon from '@material-ui/icons/Event';
import PersonIcon from '@material-ui/icons/Person';
import LinkIcon from '@material-ui/icons/Link';
import EmailIcon from '@material-ui/icons/Email';
import DescriptionIcon from '@material-ui/icons/Description';

//staffing components
import SelectMenu from 'components/Staffing/SelectMenu/SelectMenu';

import './projectCard.scss';

//actions & selectors
import { selectProject } from 'redux/selectors/staffing/getProject';
import { openAddProjectToDealDialog, deleteProject } from 'redux/actions/staffingActions';
import ProjectStatusView from '../ProjectDetailCard/ProjectStatusView';
import { showAppOverlaySpinner } from 'redux/actions/facilitatorSessionActions';
import { enqueueSuccessSnackbar } from 'redux/actions/notifierActions';
import { MEETING_PROVIDERS } from 'constants/staffing';
import GoogleMeetProvisioningFailedNotice from 'components/Staffing/Projects/GoogleMeetProvisioningFailedNotice';
import NeedsResyncCalendarNotice from 'components/Staffing/Projects/NeedsResyncCalendarNotice';

const useStyles = makeStyles(theme => ({
  jobTitle: {
    fontSize: 15,
    fontWeight: '500',
    color: 'black',
    marginBottom: 0,
  },
  jobSubtitle: {
    fontSize: 13,
    fontWeight: '400',
    color: 'black',
  },
  jobDescription: {
    fontSize: 14,
    fontWeight: '300',
    color: '#5E5E5E',
  },
  jobInfoCols: {
    paddingTop: 5,
  },
  jobInfoColTitle: {
    fontSize: 11,
    fontWeight: '400',
    color: 'rgba(0, 0, 0, 0.54)',
    marginBottom: 0,
    textTransform: 'uppercase',
  },
  jobInfoColSubTitle: {
    fontSize: 16,
    fontWeight: '400',
    color: '#333333',
  },
  jobSeatItemWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '10px 0',
  },
  controllerButton: {
    fontSize: 14,
  },
  signedLabel: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.75,
    padding: '0px 10px',
    color: '#898989',
  },
  rejectedWrapper: {
    display: 'flex',
    gap: 10,
  },
  rejectedIcon: {
    margin: 'auto',
    marginLeft: 6,
    cursor: 'pointer',
    color: '#898989',
  },

  infoLabel: {
    fontSize: 11,
    fontWeight: '400',
    color: 'rgba(0, 0, 0, 0.54)',
    textTransform: 'uppercase',
  },
  moreDetailsBtnIcon: {
    fontSize: 15,
    marginRight: 5,
  },

  rolesFilledSection: {
    marginTop: 10,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
  rolesFilledTitle: {
    fontSize: 14,
    fontWeight: '500',
    color: '#5E5E5E',
    marginBottom: 0,
  },
  // Sit both compact Alerts on the right of "Roles Filled" without stretching them.
  notices: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    '& > *:not(:last-child)': {
      marginRight: 8,
    },
  },
}));

const truncateText = (text, length = 200) =>
  text?.length > length ? `${text.slice(0, length)}...` : text;

const ProjectCard = ({ id }) => {
  const confirm = useConfirm();
  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useStyles();

  const getProject = selectProject();
  const project = useSelector(state => getProject(state, id));

  if (!project) {
    return null;
  }

  const {
    eventName,
    notes,
    formattedStartDateLocal,
    formattedStartTimeLocal,
    formattedEndDateLocal,
    formattedEndTimeLocal,
    formattedTimezone,
    dealName,
    clientName,
  } = project;

  const confirmDeleteProject = () => {
    confirm({
      description: `You are about to delete this project. Are you sure?`,
    }).then(() => {
      dispatch(showAppOverlaySpinner(true));
      dispatch(
        deleteProject(
          project.id,
          project.dealId,
          () => {
            dispatch(showAppOverlaySpinner(false));
            dispatch(enqueueSuccessSnackbar());
          },
          () => {
            dispatch(showAppOverlaySpinner(false));
          },
        ),
      );
    });
  };

  /**
   * Navigate to the project detail (jobs) view.
   * Invoked from the card link and the context menu — menu clicks do not pass a DOM event.
   */
  const handleDetailNavigate = () => {
    history.push(`/admin/staffing/projects/detail/${id}`);
  };

  // MENU CONTROL OPTIONS
  const menuOptions = [
    // {
    //   title: "Invoice",
    //   onItemClick: () => {},
    // },
    // {
    //   title: "Re-create assets",
    //   onItemClick: () => {},
    // },
    {
      title: 'View Jobs',
      onItemClick: handleDetailNavigate,
    },
    {
      title: 'Edit',
      onItemClick: () => {
        dispatch(openAddProjectToDealDialog(true, project.dealId, project.id));
      },
      disabled: project.isCreatingAttendeeAssets,
    },
    {
      title: 'Delete',
      onItemClick: confirmDeleteProject,
      disabled: project.isCreatingAttendeeAssets,
    },
  ];

  return (
    <Card className={cx('deal-project-card position-relative')}>
      <CardContent>
        <div className="d-flex justify-content-between align-items-start">
          <a
            className="deal-project-header position-relative pr-1 d-block"
            onClick={e => {
              // Keep React Router navigation; avoid a full page reload from href.
              e.preventDefault();
              handleDetailNavigate();
            }}
            href={`/admin/staffing/projects/detail/${id}`}
          >
            <p className={classes.jobTitle}>{eventName}</p>
            <div className={classes.jobSubtitle}>
              <div>{dealName}</div>
              <div>{clientName}</div>
              <div>
                {formattedStartDateLocal} <b>{formattedStartTimeLocal}</b> - {formattedEndDateLocal}{' '}
                <b>{formattedEndTimeLocal}</b> ({formattedTimezone})
              </div>
            </div>
            <p className={classes.jobSubtitle}></p>
            <p className={classes.jobDescription}>{truncateText(notes)}</p>
          </a>
          <div className="d-flex justify-content-end align-items-start flex-1">
            <div style={{ marginRight: -8 }}>
              <ProjectStatusView status={project.status} statusError={project.statusError} />
            </div>
            <div className="position-relative" style={{ marginTop: -12, marginRight: -12 }}>
              <SelectMenu options={menuOptions} menuContainerClassName="position-relative" />
            </div>
          </div>
        </div>
        <Divider />

        <Grid container spacing={5} className={classes.jobInfoCols}>
          <Grid item xs={3}>
            <p className={classes.jobInfoColTitle}>Amount</p>
            <p className={classes.jobInfoColSubTitle}>{project.formattedProjectContractValue}</p>
          </Grid>
          <Grid item xs={3}>
            <p className={classes.jobInfoColTitle}>Type</p>
            <p className={classes.jobInfoColSubTitle}>{project.displayLocationType}</p>
          </Grid>
          <Grid item xs={3}>
            <p className={classes.jobInfoColTitle}>Methodology</p>
            <p className={classes.jobInfoColSubTitle}>{project.projectMethodologyName}</p>
          </Grid>
          <Grid item xs={3}>
            <p className={classes.jobInfoColTitle}>Service Type</p>
            <p className={classes.jobInfoColSubTitle}>{project.serviceType}</p>
          </Grid>
        </Grid>

        <div>
          <p className={classes.infoLabel}>MORE DETAILS</p>

          <div className="d-flex align-items-center flex-wrap">
            <PillButton type="plainText" text={project.isPublic ? 'Public' : 'Private'} disabled />
            {project.isFree && <PillButton type="plainText" text="Free" disabled />}
            {project.isCreatingAttendeeAssets && (
              <PillButton type="plainText" text="Creating Attendee Assets" disabled loading />
            )}
            {project.venue && project.locationType !== 'virtual' && (
              <PillButton type="plainText" text={project.venue} Icon={LocationOnIcon} />
            )}
            {project.projectFolder?.webViewLink && (
              <PillButton
                type="externalUrl"
                externalUrl={project.projectFolder.webViewLink}
                text="Folder"
                Icon={FolderIcon}
              />
            )}
            <PillButton
              type="clickToCopy"
              clickToCopyContent={`${process.env.REACT_APP_HOSTING_ENV === 'prod' ? 'projects' : 'projectsdev'}+${project.id}@voltagecontrol.com`}
              copySuccessMessage="Email Copied"
              text="Email"
              Icon={EmailIcon}
            />
            <PillButton
              type="clickToCopy"
              clickToCopyContent={`${process.env.REACT_APP_HOSTING_ENV === 'prod' ? 'notetaker' : 'notetakerdev'}+${project.id}@voltagecontrol.com`}
              copySuccessMessage="Email Copied"
              text="Notetaker"
              Icon={DescriptionIcon}
            />
            {project.runOfShowDoc?.webViewLink && (
              <PillButton
                type="externalUrl"
                externalUrl={project.runOfShowDoc.webViewLink}
                text="ROS"
                Icon={LinkIcon}
              />
            )}
            {project.projectBrief?.webViewLink && (
              <PillButton
                type="externalUrl"
                externalUrl={project.projectBrief.webViewLink}
                text="Brief"
                Icon={LinkIcon}
              />
            )}
            {project.checklistDoc?.webViewLink && (
              <PillButton
                type="externalUrl"
                externalUrl={project.checklistDoc.webViewLink}
                text="TODO"
                Icon={LinkIcon}
              />
            )}

            {project.calendarEvent?.webViewLink && (
              <PillButton
                type="externalUrl"
                externalUrl={project.calendarEvent.webViewLink}
                text="Cal"
                Icon={EventIcon}
              />
            )}
            {/* Same join-url rules as ProjectDetail (Meet prefers meetingLink, fallback zoomLink). */}
            {['hybrid', 'virtual'].includes(project.locationType) &&
              (project.zoomLink || project.meetingLink) && (
                <PillButton
                  type="externalUrl"
                  externalUrl={
                    project.meetingProvider === MEETING_PROVIDERS.googleMeet
                      ? project.meetingLink || project.zoomLink
                      : project.zoomLink
                  }
                  text={project.meetingProvider === MEETING_PROVIDERS.googleMeet ? 'Meet' : 'Zoom'}
                  Icon={VideocamIcon}
                />
              )}
          </div>
        </div>

        <Divider />

        <div className={classes.rolesFilledSection}>
          <div className="d-flex align-items-center">
            <PersonIcon className={classes.moreDetailsBtnIcon} />
            <p className={classes.rolesFilledTitle}>
              {project.numberOfFilledJobs}/{size(project.jobs)} Roles Filled
            </p>
          </div>
          {(project.remindUserToRunResyncCalendar || project.googleMeetCohostsError) && (
            <div className={classes.notices}>
              {project.remindUserToRunResyncCalendar && <NeedsResyncCalendarNotice />}
              <GoogleMeetProvisioningFailedNotice errorMessage={project.googleMeetCohostsError} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectCard;

ProjectCard.propTypes = {
  id: PropTypes.string.isRequired,
};
